/**
 * Field-Level Encryption for PHI in Firestore
 *
 * Uses Web Crypto API (AES-256-GCM) to encrypt sensitive fields before
 * writing to Firestore and decrypt after reading.
 *
 * Encrypted fields: ssn, insurance.memberId, insurance.groupNumber,
 * medicalHistory, allergies, currentMedications
 *
 * Key management:
 * - For production: encryption key should come from a Firebase Cloud Function
 *   via an authenticated endpoint, never stored client-side.
 * - For development: uses a derived key from user UID + app secret.
 *   This is NOT production-grade — replace with server-side key management.
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits for AES-GCM

/**
 * Derive an encryption key from a passphrase using PBKDF2.
 * In production, replace this with a server-provided key.
 */
async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt a plaintext string under an arbitrary key context.
 * Returns a base64-encoded string containing: salt + IV + ciphertext
 *
 * `keyContext` is the passphrase fed to PBKDF2. Patient fields pass the user's
 * UID (see encryptField); records that must be readable by more than one user
 * pass a shared context instead — see eligibilityKeyContext().
 */
export async function encryptWithContext(
  plaintext: string,
  keyContext: string
): Promise<string> {
  const encoder = new TextEncoder();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const key = await deriveKey(keyContext, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    encoder.encode(plaintext)
  );

  // Combine: salt (16) + iv (12) + ciphertext
  const combined = new Uint8Array(salt.length + iv.length + ciphertext.byteLength);
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(ciphertext), salt.length + iv.length);

  return btoa(String.fromCharCode.apply(null, Array.from(combined)));
}

/**
 * Encrypt a patient field. Key is derived from the acting user's UID.
 */
export async function encryptField(
  plaintext: string,
  userUid: string
): Promise<string> {
  return encryptWithContext(plaintext, userUid);
}

/**
 * Decrypt a base64-encoded string produced by encryptWithContext.
 */
export async function decryptWithContext(
  encrypted: string,
  keyContext: string
): Promise<string> {
  const decoder = new TextDecoder();
  const combined = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0));

  const salt = combined.slice(0, 16);
  const iv = combined.slice(16, 16 + IV_LENGTH);
  const ciphertext = combined.slice(16 + IV_LENGTH);

  const key = await deriveKey(keyContext, salt);

  const plaintext = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    ciphertext
  );

  return decoder.decode(plaintext);
}

/**
 * Decrypt a patient field. Key is derived from the acting user's UID.
 */
export async function decryptField(
  encrypted: string,
  userUid: string
): Promise<string> {
  return decryptWithContext(encrypted, userUid);
}

/**
 * List of Patient fields that should be encrypted before Firestore write.
 */
export const ENCRYPTED_PATIENT_FIELDS = [
  'medicalHistory',
  'allergies',
  'currentMedications',
] as const;

/**
 * List of insurance sub-fields that should be encrypted.
 */
export const ENCRYPTED_INSURANCE_FIELDS = [
  'policyNumber',
  'groupNumber',
] as const;

/**
 * Encrypt sensitive fields in a patient object before writing to Firestore.
 */
export async function encryptPatientFields(
  patientData: Record<string, any>,
  userUid: string
): Promise<Record<string, any>> {
  const encrypted = { ...patientData };

  for (const field of ENCRYPTED_PATIENT_FIELDS) {
    if (encrypted[field] && typeof encrypted[field] === 'string') {
      encrypted[field] = await encryptField(encrypted[field], userUid);
    }
  }

  if (encrypted.insurance) {
    encrypted.insurance = { ...encrypted.insurance };
    for (const field of ENCRYPTED_INSURANCE_FIELDS) {
      if (encrypted.insurance[field] && typeof encrypted.insurance[field] === 'string') {
        encrypted.insurance[field] = await encryptField(encrypted.insurance[field], userUid);
      }
    }
  }

  return encrypted;
}

/**
 * Decrypt sensitive fields in a patient object after reading from Firestore.
 */
export async function decryptPatientFields(
  patientData: Record<string, any>,
  userUid: string
): Promise<Record<string, any>> {
  const decrypted = { ...patientData };

  for (const field of ENCRYPTED_PATIENT_FIELDS) {
    if (decrypted[field] && typeof decrypted[field] === 'string') {
      try {
        decrypted[field] = await decryptField(decrypted[field], userUid);
      } catch {
        // Field may not be encrypted (legacy data) — leave as-is
      }
    }
  }

  if (decrypted.insurance) {
    decrypted.insurance = { ...decrypted.insurance };
    for (const field of ENCRYPTED_INSURANCE_FIELDS) {
      if (decrypted.insurance[field] && typeof decrypted.insurance[field] === 'string') {
        try {
          decrypted.insurance[field] = await decryptField(decrypted.insurance[field], userUid);
        } catch {
          // Field may not be encrypted (legacy data) — leave as-is
        }
      }
    }
  }

  return decrypted;
}

// ─── Eligibility check payloads ───────────────────────────────────────
//
// The raw 271 from Stedi is the most sensitive artifact this feature touches:
// it carries the member ID, DOB, plan details, and often the full subscriber
// address. It is stored encrypted in `eligibility_checks.rawResponse`.
//
// Why these do NOT key off the user UID like patient fields do:
// the 271 is written by /api/verify-eligibility (server-side) and read later by
// whichever staff member opens the patient — a per-user key would make the
// record undecryptable by everyone except its writer. Eligibility records key
// off a practice-scoped context instead.
//
// ⚠️ KEY MANAGEMENT — UNRESOLVED, decide before Phase 5 production cutover.
// A practice ID is an identifier, not a secret. Pass `serverSecret` (from an
// env var that never reaches the browser bundle) to make this real. Until that
// is wired, treat this as obfuscation-at-rest layered on top of Firestore's
// own encryption and access rules — not as an independent control.

const ELIGIBILITY_KEY_PREFIX = 'eligibility';

/**
 * Build the key context for a practice's eligibility records.
 * Pass the server-held secret wherever one is available.
 */
export function eligibilityKeyContext(practiceId: string, serverSecret?: string): string {
  return serverSecret
    ? `${ELIGIBILITY_KEY_PREFIX}:${practiceId}:${serverSecret}`
    : `${ELIGIBILITY_KEY_PREFIX}:${practiceId}`;
}

/**
 * Encrypt a raw eligibility response for storage.
 * Accepts the parsed object; serialization happens here so callers cannot
 * accidentally persist the plaintext JSON they stringified themselves.
 */
export async function encryptEligibilityResponse(
  response: unknown,
  keyContext: string
): Promise<string> {
  return encryptWithContext(JSON.stringify(response), keyContext);
}

/**
 * Decrypt a stored eligibility response.
 * Returns null when the payload cannot be decrypted or parsed — callers render
 * the normalized `benefits` fields regardless, so a bad blob degrades the
 * "view raw response" affordance rather than the whole card.
 */
export async function decryptEligibilityResponse<T = unknown>(
  encrypted: string,
  keyContext: string
): Promise<T | null> {
  try {
    return JSON.parse(await decryptWithContext(encrypted, keyContext)) as T;
  } catch {
    return null;
  }
}
